/** Bron van waarheid voor de netwerkstatus. Offline is de normale toestand. */
export function isOnline(): boolean {
    if (typeof navigator === 'undefined') {
        return true;
    }

    return navigator.onLine !== false;
}

export type NetworkListener = (online: boolean) => void;

export function subscribeToNetwork(listener: NetworkListener): () => void {
    if (typeof window === 'undefined') {
        return () => {};
    }

    const handleOnline = () => listener(true);
    const handleOffline = () => listener(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
    };
}

/** Zo lang moet het tabblad weg zijn geweest voordat terugkomen telt als "wakker worden". */
const AWAY_LONG_ENOUGH_MS = 30_000;

/**
 * Het tabblad komt terug na een tijd op de achtergrond, of de pagina komt uit de
 * cache van de browser terug (terug-knop, laptop opengeklapt). Een verzoek van
 * vóór die pauze kan dan dood zijn zonder dat de browser het merkt, en wat in de
 * backoff stond hoeft niet langer te wachten.
 */
export function subscribeToWake(listener: () => void): () => void {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
        return () => {};
    }

    let hiddenSince: number | null = document.visibilityState === 'hidden' ? Date.now() : null;

    const handleVisibility = () => {
        if (document.visibilityState === 'hidden') {
            hiddenSince ??= Date.now();

            return;
        }

        const away = hiddenSince === null ? 0 : Date.now() - hiddenSince;
        hiddenSince = null;

        if (away >= AWAY_LONG_ENOUGH_MS) {
            listener();
        }
    };
    const handlePageShow = (event: PageTransitionEvent) => {
        if (event.persisted) {
            listener();
        }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('pageshow', handlePageShow);
    };
}
